import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {Box, Button, IconButton, LinearProgress, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {SurveyMediaValue} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {
    OFFERED_SURVEY_MEDIA_KINDS,
    SurveyMediaKind
} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {RunnerQuestionProps} from "./runnerQuestion.types";

// Keep in step with DOCUMENT_EXTENSIONS in the API's SurveyPublicServiceV3: it turns down anything else.
const ACCEPT_BY_KIND: Partial<Record<SurveyMediaKind, string>> = {
    [SurveyMediaKind.IMAGE]: 'image/*',
    [SurveyMediaKind.FILE]: '.pdf,.doc,.docx,.xls,.xlsx',
};

const MAX_FILES = 10;

const PICK_BUTTON_MIN_WIDTH = 220;

const ROW_REMOVE_SIZE = 44;

type UploadRow = {
    key: string
    name: string
    media?: SurveyMediaValue
    localFile?: File
    progress?: number
    failed?: boolean
};

const rowKey = () => `file-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const nameOfMedia = (media: SurveyMediaValue) => (
    media.fileName || decodeURIComponent(media.mediaURI?.split('/').pop() ?? '') || (media.publicId ?? '')
);

function UploadQuestion({question, localize, language, value, onAnswerChange, services}: RunnerQuestionProps) {
    const {t} = useTranslation(undefined, {lng: language});

    const inputRef = useRef<HTMLInputElement>(null);

    const [rows, setRows] = useState<UploadRow[]>(() => (
        [
            ...(value?.mediaValues ?? []).map(media => ({key: rowKey(), name: nameOfMedia(media), media})),
            ...(value?.localFiles ?? []).map(localFile => ({key: rowKey(), name: localFile.name, localFile})),
        ]
    ));

    const rowsRef = useRef(rows);
    const valueRef = useRef(value);
    useEffect(() => {
        valueRef.current = value;
    });

    const multiple = !!question.allowMultiple;
    const questionId = question.id;
    const uploadMedia = services?.uploadMedia;

    const apply = (next: UploadRow[]) => {
        rowsRef.current = next;
        setRows(next);

        onAnswerChange?.({
            ...valueRef.current,
            mediaValues: next
                .map(row => row.media)
                .filter((media): media is SurveyMediaValue => !!media),
            localFiles: next.flatMap(row => row.localFile ? [row.localFile] : []),
        });
    };

    const patch = (key: string, change: Partial<UploadRow>) => apply(
        rowsRef.current.map(row => (row.key === key ? {...row, ...change} : row))
    );

    const allowed = (question.allowMediaKinds ?? []).filter(kind => OFFERED_SURVEY_MEDIA_KINDS.includes(kind));
    const accept = (allowed.length ? allowed : OFFERED_SURVEY_MEDIA_KINDS).map(kind => ACCEPT_BY_KIND[kind]).join(',');

    const label = localize(question.buttonLabel)
        || (multiple ? t('Upload files') : t('Upload a file'));

    const pick = (picked: FileList | null) => {
        if (!picked?.length) return;

        const transferring = !!uploadMedia && !!questionId;
        const added = Array.from(picked).map(file => ({
            file,
            row: {
                key: rowKey(), name: file.name,
                progress: transferring ? 0 : undefined,
                localFile: transferring ? undefined : file,
            } as UploadRow,
        }));

        const kept = multiple ? rowsRef.current : [];
        const room = Math.max(0, (multiple ? MAX_FILES : 1) - kept.length);
        const accepted = added.slice(0, room);

        apply([...kept, ...accepted.map(entry => entry.row)]);

        if (!uploadMedia || !questionId) return;

        accepted.forEach(({file, row}) => {
            uploadMedia(questionId, file, percent => patch(row.key, {progress: percent}))
                .then(media => patch(row.key, {media, progress: undefined}))
                .catch(() => patch(row.key, {failed: true, progress: undefined}));
        });
    };

    const remove = (key: string) => apply(rowsRef.current.filter(row => row.key !== key));

    return (
        <Stack gap={1.5} alignItems={'flex-start'}>
            <Button
                variant={'outlined'}
                disableElevation
                startIcon={<CloudUploadIcon/>}
                onClick={() => inputRef.current?.click()}
                sx={{minWidth: PICK_BUTTON_MIN_WIDTH, minHeight: 48}}
            >
                {label}
            </Button>

            <Box
                component={'input'}
                ref={inputRef}
                type={'file'}
                accept={accept}
                multiple={multiple}
                sx={{display: 'none'}}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    pick(event.target.files);
                    event.target.value = '';
                }}
            />

            {!!rows.length && (
                <Stack gap={1} sx={{width: '100%', maxWidth: 440}}>
                    {rows.map(row => (
                        <Box
                            key={row.key}
                            sx={{
                                px: 1.5,
                                py: 1,
                                border: '1px solid',
                                borderColor: row.failed ? 'error.main' : 'divider',
                                borderRadius: 1,
                                transition: 'border-color 150ms ease',
                            }}
                        >
                            <Stack direction={'row'} alignItems={'center'} gap={1}>
                                {row.failed && (
                                    <ErrorOutlineIcon aria-hidden fontSize={'small'} sx={{color: 'error.main'}}/>
                                )}
                                <Typography
                                    variant={'body2'}
                                    noWrap
                                    sx={{flex: 1, minWidth: 0}}
                                    color={row.failed ? 'error.main' : 'text.primary'}
                                >
                                    {row.failed ? t('{{name}} could not be uploaded', {name: row.name}) : row.name}
                                </Typography>
                                <IconButton
                                    aria-label={t('Remove')}
                                    onClick={() => remove(row.key)}
                                    sx={{width: ROW_REMOVE_SIZE, height: ROW_REMOVE_SIZE, flexShrink: 0}}
                                >
                                    <DeleteOutlineIcon fontSize={'small'}/>
                                </IconButton>
                            </Stack>

                            {row.progress !== undefined && (
                                <LinearProgress
                                    variant={'determinate'}
                                    value={row.progress}
                                    aria-label={t('Uploading')}
                                    sx={{mt: 1, height: 4, borderRadius: 1}}
                                />
                            )}
                        </Box>
                    ))}
                </Stack>
            )}
        </Stack>
    );
}

export default UploadQuestion;
